import {
    Box,
    Text,
    HStack,
    Spinner,
    Center,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetAllInventoryActivityLogs } from '../../hooks/inventory/useGetInventoryActivityLogs';
import {
    INVENTORY_EVENT_TYPE_COLORS,
    INVENTORY_EVENT_TYPE_LABELS,
    groupInventoryLogsByDate,
    renderInventoryLogCard,
} from '../../components/inventory/inventoryActivityLogShared';
import { INSTANT_SIDE_DRAWER_MOTION_PROPS } from '../sideDrawerContainerProps';
import Timeline, { TimelineGroupData } from '../../components/Timeline';
import AppDrawer from '../../components/AppDrawer';
import {
    ActivityFeedFilters,
    EMPTY_ACTIVITY_FEED_FILTERS,
    hasActiveActivityFeedFilters,
    useActivityFeedFilterUsers,
    type ActivityFeedFiltersValue,
} from '../../components/activityFeedFilters';
import { Button, TextField } from '../../ui';

const TYPE_FILTER_OPTIONS = Object.entries(INVENTORY_EVENT_TYPE_LABELS).map(([value, label]) => ({ value, label }));

const PAGE_SIZE = 50;

interface AllInventoryActivityLogDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onItemClick?: (inventoryItemId: string) => void;
    onImportActivityClick?: (jobId: string) => void;
    onExportActivityClick?: (exportId: string) => void;
}

const AllInventoryActivityLogDrawer: React.FC<AllInventoryActivityLogDrawerProps> = ({
    isOpen,
    onClose,
    onItemClick,
    onImportActivityClick,
    onExportActivityClick,
}) => {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filters, setFilters] = useState<ActivityFeedFiltersValue>({ ...EMPTY_ACTIVITY_FEED_FILTERS });
    const filterUsers = useActivityFeedFilterUsers();

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch((prev) => {
                if (prev !== search.trim()) setPage(1);
                return search.trim();
            });
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const { data, isLoading, isError, isFetching } = useGetAllInventoryActivityLogs(
        page,
        PAGE_SIZE,
        isOpen,
        debouncedSearch,
        filters,
    );

    const totalPages = Math.max(1, Math.ceil((data?.totalCount ?? 0) / PAGE_SIZE));
    const logs = useMemo(() => data?.logs ?? [], [data?.logs]);

    const handleFiltersChange = (next: ActivityFeedFiltersValue) => {
        setFilters(next);
        setPage(1);
    };

    const timelineGroups: TimelineGroupData[] = useMemo(() => {
        const grouped = groupInventoryLogsByDate(logs);
        return Array.from(grouped.entries()).map(([label, items]) => ({
            key: label,
            label,
            rightLabel: `${items.length}`,
            items: items.map((log) => ({
                key: log._id,
                nodeColor: INVENTORY_EVENT_TYPE_COLORS[log.eventType] ?? 'brand.solid',
                content: renderInventoryLogCard(log, {
                    showItemRef: true,
                    flat: true,
                    onItemClick: onItemClick
                        ? (id) => {
                              if (log.eventType === 'item-deleted') return;
                              onClose();
                              onItemClick(id);
                          }
                        : undefined,
                    onImportActivityClick: onImportActivityClick
                        ? (jobId) => {
                              onClose();
                              onImportActivityClick(jobId);
                          }
                        : undefined,
                    onExportActivityClick: onExportActivityClick
                        ? (exportId) => {
                              onClose();
                              onExportActivityClick(exportId);
                          }
                        : undefined,
                }),
            })),
        }));
    }, [logs, onClose, onItemClick, onImportActivityClick, onExportActivityClick]);

    return (
        <AppDrawer
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            title={
                <HStack spacing={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="fg.default">
                        Activity Logs
                    </Text>
                    {!isLoading && (
                        <Text fontSize="xs" color="fg.muted">
                            {data?.totalCount ?? 0} event{(data?.totalCount ?? 0) === 1 ? '' : 's'}
                        </Text>
                    )}
                </HStack>
            }
            contentProps={{
                motionProps: INSTANT_SIDE_DRAWER_MOTION_PROPS,
                overflow: 'hidden',
            }}
            bodyProps={{ px: 5, py: 4, pt: 4, bg: 'bg.surface' }}
            headerBelow={
                <Box px={5} py={3} borderBottom="1px solid" borderColor="border.subtle">
                    <ActivityFeedFilters
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        eventTypeOptions={TYPE_FILTER_OPTIONS}
                        users={filterUsers}
                        searchSlot={(
                            <TextField
                                size="sm"
                                placeholder="Search activity or part number…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                h="32px"
                            />
                        )}
                    />
                </Box>
            }
            bottomBar={
                totalPages > 1 ? (
                    <HStack
                        justify="center"
                        spacing={3}
                        py={2}
                        bg="bg.surface"
                        borderTop="1px solid"
                        borderColor="border.subtle"
                    >
                        <Button
                            size="xs"
                            variant="outlineGray"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            isDisabled={page <= 1 || isFetching}
                        >
                            Previous
                        </Button>
                        <Text fontSize="xs" color="fg.muted">
                            Page {page} of {totalPages}
                        </Text>
                        <Button
                            size="xs"
                            variant="outlineGray"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            isDisabled={page >= totalPages || isFetching}
                        >
                            Next
                        </Button>
                    </HStack>
                ) : undefined
            }
        >
            {isLoading && (
                <Center py={12}>
                    <Spinner color="brand.solid" />
                </Center>
            )}
            {isError && (
                <Text fontSize="sm" color="red.500" textAlign="center" py={8}>
                    Failed to load activity logs
                </Text>
            )}
            {!isLoading && !isError && logs.length === 0 && (
                <Text fontSize="sm" color="fg.muted" fontStyle="italic" textAlign="center" py={8}>
                    {debouncedSearch || hasActiveActivityFeedFilters(filters)
                        ? 'No activity matches your filters'
                        : 'No activity logged yet'}
                </Text>
            )}
            {!isLoading && !isError && logs.length > 0 && (
                <Timeline groups={timelineGroups} />
            )}
        </AppDrawer>
    );
};

export default AllInventoryActivityLogDrawer;
